<template>
  <el-divider content-position="left">Fórmula</el-divider>
  <el-row>
    <el-col :span="24">
      <el-table
        :data="modelValue"
        border
        size="small"
        style="width: 100%"
      >
        <el-table-column
          :label="columnaLabel"
          min-width="300"
        >
          <template #default="{ row }">
            <el-select
              v-model="row.CodigoMaterialFormula"
              placeholder="Seleccionar material"
              filterable
              style="width: 100%"
            >
              <el-option
                v-for="m in materiales"
                :key="m.Codigo"
                :label="m.DescripcionInterna"
                :value="m.Codigo"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column
          label="Proporción (%)"
          width="180"
        >
          <template #default="{ row }">
            <el-input-number
              ref="inputRef"
              v-model="row.Proporcion"
              :min="0"
              :max="100"
              :precision="2"
              :step="1"
              :controls="false"
              :formatter="formatter"
              :parser="parser"
              style="width: 100%"
              @focus="handleFocus"
              @blur="handleBlur(row.Proporcion)"
            />
          </template>
        </el-table-column>
        <el-table-column
          label="Densidad"
          width="160"
          align="center"
        >
          <template #default="{ row }">
            <el-input
              :model-value="getPropiedadComponente(row.CodigoMaterialFormula, 'Densidad')"
              disabled
              style="width: 100%"
            />
          </template>
        </el-table-column>
        <el-table-column
          label="Gramaje"
          width="160"
          align="center"
        >
          <template #default="{ row }">
            <el-input
              :model-value="getPropiedadComponente(row.CodigoMaterialFormula, 'Gramaje')"
              disabled
              style="width: 100%"
            />
          </template>
        </el-table-column>
        <el-table-column
          label="Acción"
          width="100"
          align="center"
        >
          <template #default="{ $index }">
            <el-button
              type="danger"
              size="small"
              @click="removeRow($index)"
              >Eliminar</el-button
            >
          </template>
        </el-table-column>
      </el-table>
      <div style="margin-top: 8px; display: flex; align-items: center; gap: 16px">
        <el-button
          size="small"
          @click="addRow"
          >+ Agregar material</el-button
        >
        <span
          v-if="modelValue?.length"
          style="font-size: 13px; color: #606266"
        >
          Total: <strong>{{ formatNumberLocale(totalProporcion, 2) }}%</strong>
        </span>
      </div>
    </el-col>
  </el-row>
</template>

<script setup>
import {
  computed,
  ref,
  watch,
} from 'vue';
import { MaterialService } from '@/services/material_service';
import { calcularPropiedadesDesdeFormula } from '@/shared/utils/material_formula_calculos.js';
import { formatNumberLocale } from '@/shared/utils/locale_number_utils.js';
import { useLocaleNumberInput } from '@/shared/composables/useLocaleNumberInput.js';

const { inputRef, formatter, parser, handleFocus, handleBlur } = useLocaleNumberInput(2);

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  tipoItem: { type: [String, Number], default: null },
  columnaLabel: { type: String, default: 'Mezcla' },
});

const emit = defineEmits(['update:modelValue', 'propiedades-calculadas']);

const materiales = ref([]);

watch(
  () => props.tipoItem,
  async (tipo, _prev, onCleanup) => {
    if (!tipo) {
      materiales.value = [];
      return;
    }
    let vigente = true;
    onCleanup(() => {
      vigente = false;
    });
    try {
      const res = await MaterialService.findAll({
        tipoItem: tipo,
        limit: 200,
        sortBy: 'DescripcionInterna',
        sort: 'Asc',
      });
      if (vigente) materiales.value = res?.data ?? [];
    } catch {
      if (vigente) materiales.value = [];
    }
  },
  { immediate: true },
);

const totalProporcion = computed(() => {
  if (!props.modelValue?.length) return 0;
  return props.modelValue.reduce((sum, row) => sum + (Number(row.Proporcion) || 0), 0);
});

const getPropiedadComponente = (codigo, propiedad) => {
  if (!codigo) return null;
  const mat = materiales.value.find((m) => m.Codigo === codigo);
  const valor = mat?.[propiedad];
  return valor != null ? Number(valor).toFixed(4) : null;
};

// La densidad y el gramaje del material se derivan de su fórmula: se recalculan ante
// cualquier cambio de las filas o del catálogo (que llega asincrónico).
watch(
  [() => props.modelValue, materiales],
  () => {
    emit(
      'propiedades-calculadas',
      calcularPropiedadesDesdeFormula(props.modelValue, materiales.value),
    );
  },
  { deep: true, immediate: true },
);

const addRow = () => {
  const rows = Array.isArray(props.modelValue) ? props.modelValue : [];
  emit('update:modelValue', [...rows, { CodigoMaterialFormula: null, Proporcion: 0 }]);
};

const removeRow = (index) => {
  const rows = [...props.modelValue];
  rows.splice(index, 1);
  emit('update:modelValue', rows);
};
</script>
